import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { 
  Fabric, fabricTypes, fabricClasses, dyeingTypes,
  fabricClassLabelToValue, fabricClassValueToLabel,
  fabricTypeLabelToValue, fabricTypeValueToLabel,
  dyeingTypeLabelToValue, dyeingTypeValueToLabel
} from '../models/Fabric';
import { getFabricById, updateFabric } from '../services/fabricService';
import { toast } from 'react-toastify';
import { API_URL } from '../config/constants';

// Döviz seçenekleri
const currencies = [
  { value: 'TRY', label: '₺ TRY' },
  { value: 'USD', label: '$ USD' },
  { value: 'EUR', label: '€ EUR' }
];

// Birim seçenekleri
const priceUnits = [
  { value: 'meter', label: 'Metraj (m)' },
  { value: 'gram', label: 'Gramaj (gr/m²)' }
];

// Fiyatlandırma için interface tanımı
interface FabricWithPricing extends Fabric {
  currency?: string;
  priceUnit?: string;
}

const EditFabric: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const [loading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [form, setForm] = useState<Partial<FabricWithPricing>>({
    name: '',
    fabricClass: fabricClasses[0],
    type: fabricTypes[0],
    yarnNumber: '',
    composition: '',
    dyeingType: dyeingTypes[0],
    density: {
      warp: '',
      weft: ''
    },
    weight: 0,
    width: 0,
    notes: '',
    price: 0,
    currency: 'TRY',
    priceUnit: 'meter'
  });

  // Dosya yükleme için state'ler
  const [imageFile, setImageFile] = useState<File | null>(null);
  const [swatchFile, setSwatchFile] = useState<File | null>(null);
  const [imagePreview, setImagePreview] = useState<string | null>(null);
  const [swatchPreview, setSwatchPreview] = useState<string | null>(null);

  // Kumaş verilerini yükleme
  useEffect(() => {
    const loadFabric = async () => {
      try {
        setInitialLoading(true);
        if (id) {
          const data = await getFabricById(id);
          
          // Eğer density null ise, varsayılan değerlerle doldur
          if (!data.density) {
            data.density = { warp: '', weft: '' };
          }
          
          // Veritabanındaki İngilizce değerleri kullanıcı arayüzü için Türkçe'ye çevirelim
          setForm({
            name: data.name || '',
            fabricClass: fabricClassValueToLabel(data.fabricClass),
            type: fabricTypeValueToLabel(data.type),
            yarnNumber: data.yarnNumber || '',
            composition: data.composition || '',
            dyeingType: dyeingTypeValueToLabel(data.dyeingType),
            density: {
              warp: data.density?.warp || '',
              weft: data.density?.weft || ''
            },
            weight: data.weight || 0,
            width: data.width || 0,
            notes: data.notes || '',
            imageUrl: data.imageUrl || '',
            swatch: data.swatch || '',
            price: data.price || 0,
            currency: (data as any).currency || 'TRY',
            priceUnit: (data as any).priceUnit || 'meter'
          });
          
          // Mevcut görselleri önizleme olarak ayarla
          if (data.imageUrl) {
            setImagePreview(`${API_URL}${data.imageUrl.startsWith('/') ? '' : '/'}${data.imageUrl}`);
          }
          if (data.swatch) {
            setSwatchPreview(`${API_URL}${data.swatch.startsWith('/') ? '' : '/'}${data.swatch}`);
          }
        }
        setError(null);
      } catch (err) {
        console.error('Kumaş bilgileri yüklenirken hata oluştu:', err);
        setError('Kumaş bilgileri yüklenirken bir hata oluştu. Lütfen daha sonra tekrar deneyin.');
        toast.error('Kumaş bilgileri yüklenirken bir hata oluştu');
      } finally {
        setInitialLoading(false);
      }
    };

    loadFabric();
  }, [id]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    
    // Sıklık (density) bilgileri için özel işleme
    if (name === 'warp' || name === 'weft') {
      setForm(prevForm => ({
        ...prevForm,
        density: {
          ...prevForm.density || {},
          [name]: value
        }
      }));
      console.log(`[Debug] ${name} değeri güncellendi:`, value);
    } else {
      setForm(prevForm => ({ ...prevForm, [name]: value }));
      console.log(`[Debug] ${name} değeri güncellendi:`, value);
    }
  };

  const handleNumberChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    const numValue = value === '' ? 0 : parseFloat(value);
    setForm(prevForm => ({ ...prevForm, [name]: numValue }));
    console.log(`[Debug] ${name} sayısal değeri güncellendi:`, numValue);
  };

  // Resim yükleme
  const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0];
      setImageFile(file);
      setImagePreview(URL.createObjectURL(file));
    }
  };

  // Kumaş örneği yükleme
  const handleSwatchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0];
      setSwatchFile(file);
      setSwatchPreview(URL.createObjectURL(file));
    }
  };

  // Dosya kaldırma işlemleri
  const handleRemoveImage = () => {
    setImageFile(null);
    setImagePreview(null);
    setForm(prev => ({ ...prev, imageUrl: "" }));
  };
  
  const handleRemoveSwatch = () => {
    setSwatchFile(null);
    setSwatchPreview(null);
    setForm(prev => ({ ...prev, swatch: "" }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    console.log("[EditFabric] Form submit işlemi başladı");
    
    // Form validasyonu
    if (!form.name || !form.fabricClass || !form.type || !form.composition) {
      toast.error('Lütfen gerekli alanları doldurun.');
      return;
    }

    if (!id) {
      toast.error('Kumaş ID\'si bulunamadı');
      return;
    }

    try {
      setLoading(true);
      console.log("[EditFabric] Form verisi hazırlanıyor...");
      
      // Form verilerinin doğru şekilde hazırlanması - sadece gerekli alanları gönder
      const fabricData: Partial<Fabric> = {
        name: form.name || '',
        fabricClass: fabricClassLabelToValue(form.fabricClass || fabricClasses[0]),
        type: fabricTypeLabelToValue(form.type || fabricTypes[0]),
        yarnNumber: form.yarnNumber || '',
        composition: form.composition || '',
        dyeingType: dyeingTypeLabelToValue(form.dyeingType || dyeingTypes[0]),
        density: {
          warp: form.density?.warp || '',
          weft: form.density?.weft || ''
        },
        weight: typeof form.weight === 'number' ? form.weight : 0,
        width: typeof form.width === 'number' ? form.width : 0,
        notes: form.notes || '',
        price: typeof form.price === 'number' ? form.price : 0
      };
      
      // Fiyatlandırma verilerini de ekle
      if (form.currency) {
        (fabricData as any).currency = form.currency;
      }
      if (form.priceUnit) {
        (fabricData as any).priceUnit = form.priceUnit;
      }
      
      // Debug için veriyi kontrol et
      console.log('[EditFabric] API\'ye gönderilecek veri:', JSON.stringify(fabricData, null, 2));
      
      // API çağrısı yap
      const result = await updateFabric(id, fabricData, imageFile || undefined, swatchFile || undefined);
      
      console.log('[EditFabric] API yanıtı:', result);
      toast.success('Kumaş başarıyla güncellendi');
      navigate(`/fabrics/${id}`);
    } catch (error: any) {
      console.error('[EditFabric] Hata oluştu:', error);
      toast.error(`Kumaş güncellenirken bir hata oluştu: ${error.message || 'Bilinmeyen hata'}`);
    } finally {
      setLoading(false);
    }
  };

  if (initialLoading) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="flex justify-center items-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="bg-red-50 border border-red-200 text-red-800 rounded-lg p-4">
          <p>{error}</p>
          <button 
            onClick={() => navigate('/fabrics')}
            className="mt-2 text-red-600 hover:text-red-800 font-medium"
          >
            Kumaşlar listesine dön
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-8">
      <div className="flex items-center mb-6">
        <button
          onClick={() => navigate(`/fabrics/${id}`)}
          className="flex items-center mr-4 text-blue-600 hover:text-blue-800 transition duration-200"
          disabled={loading}
        >
          <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-1" viewBox="0 0 20 20" fill="currentColor">
            <path fillRule="evenodd" d="M9.707 14.707a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414l4-4a1 1 0 011.414 1.414L7.414 9H15a1 1 0 110 2H7.414l2.293 2.293a1 1 0 010 1.414z" clipRule="evenodd" />
          </svg>
          <span>Geri</span>
        </button>
        <h1 className="text-2xl font-bold text-gray-800">{form.name ? `"${form.name}" Kumaşını Düzenle` : 'Kumaş Düzenle'}</h1>
      </div>

      <form 
        onSubmit={handleSubmit} 
        encType="multipart/form-data"
        className="bg-white shadow-md rounded-lg p-6 mb-6 border border-gray-200"
      >
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
          {/* Temel Bilgiler */}
          <div className="p-4 bg-blue-50/50 rounded-lg border border-blue-100 transition-all duration-200 hover:shadow-sm">
            <h2 className="text-lg font-semibold mb-4 text-blue-800 border-b border-blue-200 pb-2 flex items-center">
              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
              </svg>
              Temel Bilgiler
            </h2>
            
            <div className="space-y-4">
              <div>
                <label className="block text-gray-700 text-sm font-medium mb-1 flex items-center">
                  Kumaş Adı 
                  <span className="text-red-600 ml-1">*</span>
                </label>
                <input
                  type="text"
                  name="name"
                  value={form.name}
                  onChange={handleChange}
                  className="w-full px-4 py-2.5 border border-gray-300 rounded-md shadow-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition duration-200"
                  required
                  placeholder="Kumaş adını girin"
                />
              </div>

              <div>
                <label className="block text-gray-700 text-sm font-medium mb-1 flex items-center">
                  Kumaş Sınıfı 
                  <span className="text-red-600 ml-1">*</span>
                </label>
                <select
                  name="fabricClass"
                  value={form.fabricClass}
                  onChange={handleChange}
                  className="w-full px-4 py-2.5 border border-gray-300 rounded-md shadow-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white transition duration-200"
                  required
                >
                  {fabricClasses.map(cls => (
                    <option key={cls} value={cls}>{cls}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-gray-700 text-sm font-medium mb-1 flex items-center">
                  Kumaş Tipi (kumaş kalitesi) 
                  <span className="text-red-600 ml-1">*</span>
                </label>
                <select
                  name="type"
                  value={form.type}
                  onChange={handleChange}
                  className="w-full px-4 py-2.5 border border-gray-300 rounded-md shadow-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white transition duration-200"
                  required
                >
                  {fabricTypes.map(type => (
                    <option key={type} value={type}>{type}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-gray-700 text-sm font-medium mb-1">
                  İplik Numarası
                </label>
                <input
                  type="text"
                  name="yarnNumber"
                  value={form.yarnNumber}
                  onChange={handleChange}
                  placeholder="Örn: 30/1, 40/2"
                  className="w-full px-4 py-2.5 border border-gray-300 rounded-md shadow-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition duration-200"
                />
              </div>

              <div>
                <label className="block text-gray-700 text-sm font-medium mb-1 flex items-center">
                  İçerik Bilgisi
                  <span className="text-red-600 ml-1">*</span>
                </label>
                <input
                  type="text"
                  name="composition"
                  value={form.composition}
                  onChange={handleChange}
                  placeholder="Örn: %100 Pamuk, %80 Polyester %20 Elastan"
                  className="w-full px-4 py-2.5 border border-gray-300 rounded-md shadow-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition duration-200"
                  required
                />
              </div>

              <div>
                <label className="block text-gray-700 text-sm font-medium mb-1">
                  Boyama Çeşidi
                </label>
                <select
                  name="dyeingType"
                  value={form.dyeingType}
                  onChange={handleChange}
                  className="w-full px-4 py-2.5 border border-gray-300 rounded-md shadow-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white transition duration-200"
                >
                  {dyeingTypes.map(type => (
                    <option key={type} value={type}>{type}</option>
                  ))}
                </select>
              </div>

              {/* Fiyatlandırma Bölümü */}
              <div className="mt-6 p-4 bg-green-50/50 rounded-lg border border-green-200">
                <h3 className="text-md font-semibold mb-4 text-green-800 border-b border-green-200 pb-2 flex items-center">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2" viewBox="0 0 20 20" fill="currentColor">
                    <path d="M8.433 7.418c.155-.103.346-.196.567-.267v1.698a2.305 2.305 0 01-.567-.267C8.07 8.34 8 8.114 8 8c0-.114.07-.34.433-.582zM11 12.849v-1.698c.22.071.412.164.567.267.364.243.433.468.433.582 0 .114-.07.34-.433.582a2.305 2.305 0 01-.567.267z" />
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-13a1 1 0 10-2 0v.092a4.535 4.535 0 00-1.676.662C6.602 6.234 6 7.009 6 8c0 .99.602 1.765 1.324 2.246.48.32 1.054.545 1.676.662v1.941c-.391-.127-.68-.317-.843-.504a1 1 0 10-1.51 1.31c.562.649 1.413 1.076 2.353 1.253V15a1 1 0 102 0v-.092a4.535 4.535 0 001.676-.662C13.398 13.766 14 12.991 14 12c0-.99-.602-1.765-1.324-2.246A4.535 4.535 0 0011 9.092V7.151c.391.127.68.317.843.504a1 1 0 101.511-1.31c-.563-.649-1.413-1.076-2.354-1.253V5z" clipRule="evenodd" />
                  </svg>
                  Fiyatlandırma
                </h3>
                
                <div className="space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div className="md:col-span-1">
                      <label className="block text-gray-700 text-sm font-medium mb-1">
                        Kumaş Fiyatı
                      </label>
                      <input
                        type="number"
                        name="price"
                        value={form.price || ''}
                        onChange={handleNumberChange}
                        min="0"
                        step="0.01"
                        placeholder="0.00"
                        className="w-full px-4 py-2.5 border border-gray-300 rounded-md shadow-sm focus:ring-2 focus:ring-green-500 focus:border-green-500 transition duration-200"
                      />
                    </div>
                    
                    <div>
                      <label className="block text-gray-700 text-sm font-medium mb-1">
                        Döviz
                      </label>
                      <select
                        name="currency"
                        value={form.currency}
                        onChange={handleChange}
                        className="w-full px-4 py-2.5 border border-gray-300 rounded-md shadow-sm focus:ring-2 focus:ring-green-500 focus:border-green-500 bg-white transition duration-200"
                      >
                        {currencies.map(currency => (
                          <option key={currency.value} value={currency.value}>{currency.label}</option>
                        ))}
                      </select>
                    </div>
                    
                    <div>
                      <label className="block text-gray-700 text-sm font-medium mb-1">
                        Birim
                      </label>
                      <select
                        name="priceUnit"
                        value={form.priceUnit}
                        onChange={handleChange}
                        className="w-full px-4 py-2.5 border border-gray-300 rounded-md shadow-sm focus:ring-2 focus:ring-green-500 focus:border-green-500 bg-white transition duration-200"
                      >
                        {priceUnits.map(unit => (
                          <option key={unit.value} value={unit.value}>{unit.label}</option>
                        ))}
                      </select>
                    </div>
                  </div>
                  
                  <div className="text-sm text-gray-600 bg-white p-3 rounded border border-gray-200">
                    <div className="flex items-center">
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-2 text-blue-500" viewBox="0 0 20 20" fill="currentColor">
                        <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                      </svg>
                      <span className="font-medium">Fiyat Özeti:</span>
                    </div>
                    <div className="mt-1 ml-6">
                      {form.price && form.currency && form.priceUnit ? (
                        <span>
                          {form.price} {currencies.find(c => c.value === form.currency)?.label} / {priceUnits.find(u => u.value === form.priceUnit)?.label}
                        </span>
                      ) : (
                        <span className="text-gray-400">Fiyat bilgisi girilmedi</span>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
            <div className="grid grid-cols-1 md:grid-cols-1 gap-8">

          {/* Teknik Detaylar ve Görseller */}
          <div className="p-4 bg-purple-50/50 rounded-lg border border-purple-100 transition-all duration-200 hover:shadow-sm">
            <h2 className="text-lg font-semibold mb-4 text-purple-800 border-b border-purple-200 pb-2 flex items-center">
              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M3 4a1 1 0 011-1h12a1 1 0 011 1v2a1 1 0 01-1 1H4a1 1 0 01-1-1V4zm0 4a1 1 0 011-1h12a1 1 0 011 1v6a1 1 0 01-1 1H4a1 1 0 01-1-1V8z" clipRule="evenodd" />
              </svg>
              Teknik Detaylar
            </h2>
            
            <div className="space-y-4">
              {/* Sıklık Bilgileri */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-gray-700 text-sm font-medium mb-1">
                    Çözgü Sıklığı
                  </label>
                  <input
                    type="text"
                    name="warp"
                    value={form.density?.warp || ''}
                    onChange={handleChange}
                    placeholder="Örn: 120"
                    className="w-full px-4 py-2.5 border border-gray-300 rounded-md shadow-sm focus:ring-2 focus:ring-purple-500 focus:border-purple-500 transition duration-200"
                  />
                </div>
                <div>
                  <label className="block text-gray-700 text-sm font-medium mb-1">
                    Atkı Sıklığı
                  </label>
                  <input
                    type="text"
                    name="weft"
                    value={form.density?.weft || ''}
                    onChange={handleChange}
                    placeholder="Örn: 80"
                    className="w-full px-4 py-2.5 border border-gray-300 rounded-md shadow-sm focus:ring-2 focus:ring-purple-500 focus:border-purple-500 transition duration-200"
                  />
                </div>
              </div>

              {/* Ağırlık ve En Bilgileri */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-gray-700 text-sm font-medium mb-1">
                    Ağırlık (gr/m²)
                  </label>
                  <input
                    type="number"
                    name="weight"
                    value={form.weight || ''}
                    onChange={handleNumberChange}
                    min="0"
                    placeholder="Örn: 150"
                    className="w-full px-4 py-2.5 border border-gray-300 rounded-md shadow-sm focus:ring-2 focus:ring-purple-500 focus:border-purple-500 transition duration-200"
                  />
                </div>
                <div>
                  <label className="block text-gray-700 text-sm font-medium mb-1">
                    En (cm)
                  </label>
                  <input
                    type="number"
                    name="width"
                    value={form.width || ''}
                    onChange={handleNumberChange}
                    min="0"
                    placeholder="Örn: 150"
                    className="w-full px-4 py-2.5 border border-gray-300 rounded-md shadow-sm focus:ring-2 focus:ring-purple-500 focus:border-purple-500 transition duration-200"
                  />
                </div>
              </div>

              {/* Notlar */}
              <div>
                <label className="block text-gray-700 text-sm font-medium mb-1">
                  Notlar
                </label>
                <textarea
                  name="notes"
                  value={form.notes || ''}
                  onChange={handleChange}
                  rows={3}
                  placeholder="Kumaş hakkında ek bilgiler..."
                  className="w-full px-4 py-2.5 border border-gray-300 rounded-md shadow-sm focus:ring-2 focus:ring-purple-500 focus:border-purple-500 transition duration-200 resize-none"
                />
              </div>

              {/* Kumaş Görseli */}
              <div className="mt-6 p-4 bg-orange-50/50 rounded-lg border border-orange-200">
                <h3 className="text-md font-semibold mb-4 text-orange-800 border-b border-orange-200 pb-2 flex items-center">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M4 3a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V5a2 2 0 00-2-2H4zm12 12H4l4-8 3 6 2-4 3 6z" clipRule="evenodd" />
                  </svg>
                  Kumaş Görseli
                </h3>
                
                <div className="space-y-4">
                  <div>
                    <label className="block text-gray-700 text-sm font-medium mb-2">
                      Kumaş Fotoğrafı
                    </label>
                    <input
                      type="file"
                      accept="image/*"
                      onChange={handleImageChange}
                      className="w-full px-4 py-2.5 border border-gray-300 rounded-md shadow-sm focus:ring-2 focus:ring-orange-500 focus:border-orange-500 bg-white transition duration-200"
                    />
                    {imagePreview && (
                      <div className="mt-3 relative">
                        <img 
                          src={imagePreview} 
                          alt="Kumaş önizleme" 
                          className="w-full h-48 object-cover rounded-lg border border-gray-200 shadow-sm"
                        />
                        <button
                          type="button"
                          onClick={handleRemoveImage}
                          className="absolute top-2 right-2 bg-red-500 hover:bg-red-600 text-white rounded-full p-1.5 shadow-lg transition duration-200"
                        >
                          <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                          </svg>
                        </button>
                      </div>
                    )}
                  </div>

                  <div>
                    <label className="block text-gray-700 text-sm font-medium mb-2">
                      Kumaş Örneği (Swatch)
                    </label>
                    <input
                      type="file"
                      accept="image/*"
                      onChange={handleSwatchChange}
                      className="w-full px-4 py-2.5 border border-gray-300 rounded-md shadow-sm focus:ring-2 focus:ring-orange-500 focus:border-orange-500 bg-white transition duration-200"
                    />
                    {swatchPreview && (
                      <div className="mt-3 relative">
                        <img 
                          src={swatchPreview} 
                          alt="Kumaş örneği önizleme" 
                          className="w-full h-32 object-cover rounded-lg border border-gray-200 shadow-sm"
                        />
                        <button
                          type="button"
                          onClick={handleRemoveSwatch}
                          className="absolute top-2 right-2 bg-red-500 hover:bg-red-600 text-white rounded-full p-1.5 shadow-lg transition duration-200"
                        >
                          <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                          </svg>
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        </div>

        <div className="flex justify-end mt-8 space-x-2 border-t pt-6">
          <button
            type="button"
            onClick={() => navigate(`/fabrics/${id}`)}
            className="px-5 py-2.5 bg-white hover:bg-gray-100 border border-gray-300 text-gray-700 font-medium rounded-md shadow-sm transition duration-200 flex items-center"
            disabled={loading}
          >
            <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-1.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
            </svg>
            İptal
          </button>
          <button
            type="submit"
            className="px-5 py-2.5 bg-blue-600 hover:bg-blue-700 text-white font-medium rounded-md shadow-sm transition duration-200 flex items-center"
            disabled={loading}
          >
            {loading ? (
              <>
                <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                Kaydediliyor...
              </>
            ) : (
              <>
                <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-1.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                </svg>
                Kaydet
              </>
            )}
          </button>
        </div>
      </form>
    </div>
  );
};

export default EditFabric;
